import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import { BulkPreviewTable } from "@/components/catalog/BulkPreviewTable";

const EMPTY = { design_name: "", color_scope: "all", color_codes: [], format_in: "", width_mm: "", length_mm: "", presentation: "Loose",
  mesh_sheet_dims: "", pieces_per_box: "", box_coverage_sqft: "", finish: "", sku_prefix: "" };
const num = (v) => (v === "" || v == null ? null : Number(v));

// ID-053: every field that blocks preview generation gets its own message (server re-checks everything)
export function fieldIssues(f) {
  const e = {};
  const pos = (k, label) => { const n = num(f[k]); if (!(n > 0)) e[k] = f[k] === "" ? `${label} is required` : `${label} must be a number above 0`; };
  if (!f.design_name.trim()) e.design_name = "Design / product name is required";
  if (!f.format_in.trim()) e.format_in = "Format is required (e.g. 1x4)";
  pos("width_mm", "Width"); pos("length_mm", "Length"); pos("pieces_per_box", "Pieces per box"); pos("box_coverage_sqft", "Sq ft per box");
  if (num(f.pieces_per_box) > 0 && !Number.isInteger(num(f.pieces_per_box))) e.pieces_per_box = "Pieces per box must be a whole number";
  if (!f.finish.trim()) e.finish = "Finish is required";
  if (!/^[A-Za-z]{2,4}$/.test(f.sku_prefix.trim())) e.sku_prefix = "SKU prefix must be 2–4 letters";
  if (f.presentation === "Mesh-mounted" && !f.mesh_sheet_dims.trim()) e.mesh_sheet_dims = "Mesh sheet size is required for mesh-mounted";
  if (f.color_scope === "selected" && !f.color_codes.length) e.color_codes = "Select at least one color";
  return e;
}
const toSpec = (f) => ({ ...f, design_name: f.design_name.trim(), format_in: f.format_in.trim(), sku_prefix: f.sku_prefix.trim().toUpperCase(),
  width_mm: num(f.width_mm), length_mm: num(f.length_mm), pieces_per_box: num(f.pieces_per_box), box_coverage_sqft: num(f.box_coverage_sqft) });

function Field({ label, k, f, set, errors, ...rest }) {
  const err = errors?.[k];
  return (
    <div>
      <Label className="text-xs text-warmgrey">{label}</Label>
      <Input value={f[k]} onChange={(e) => set(k, e.target.value)} onInput={(e) => set(k, e.target.value)} aria-invalid={!!err}
        className={`mt-1 h-10 bg-white ${err ? "border-status-sold" : ""}`} data-testid={`design-${k}`} {...rest} />
      {err && <p className="mt-1 text-xs font-medium text-status-sold" data-testid={`design-error-${k}`}>{err}</p>}
    </div>
  );
}

function Coverage({ f }) {
  const w = num(f.width_mm), l = num(f.length_mm), p = num(f.pieces_per_box), s = num(f.box_coverage_sqft);
  if (!(w && l && p)) return <p className="text-xs text-muted-foreground" data-testid="design-coverage">Enter dimensions and pieces per box to see coverage.</p>;
  const calc = Math.round(w * l * p / 92903.04 * 100) / 100;
  const off = s && Math.abs(calc - s) / s > 0.03;
  return (
    <p className={`text-xs ${off ? "font-medium text-status-reserved" : "text-status-available"}`} data-testid="design-coverage">
      {off && <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />}
      {p} pcs × {w}×{l} mm ≈ <b>{calc} sq ft</b>{s ? ` · entered ${s} sq ft${off ? " — mismatch, correct pieces or sq ft per box" : " — matches"}` : ""}
    </p>
  );
}

export default function NewDesignWizard() {
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [colors, setColors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState(null);
  const [tried, setTried] = useState(false);
  const [serverErr, setServerErr] = useState({});
  const issues = fieldIssues(f);
  const errors = tried ? { ...serverErr, ...issues } : serverErr;
  useEffect(() => { api.get("/admin/catalog/colors").then(({ data }) => setColors(data)).catch(() => {}); }, []);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setServerErr((x) => ({ ...x, [k]: undefined })); setPreview(null); setRows(null); };
  const toggleColor = (code) => set("color_codes", f.color_codes.includes(code) ? f.color_codes.filter((c) => c !== code) : [...f.color_codes, code]);
  const activeColors = useMemo(() => colors.filter((c) => c.status !== "INACTIVE"), [colors]);

  const run = async (withRows) => {
    setTried(true);
    if (Object.keys(issues).length) { toast.error(`Fix ${Object.keys(issues).length} field(s) to generate the preview`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalog/bulk/preview", { spec: toSpec(f), rows: withRows || null });
      setPreview(data);
      setRows(data.rows.map((r) => ({ color_code: r.color_code, sku: r.sku, color_name: r.color_name || "", include: r.include })));
    } catch (e) {
      const d = e.response?.data?.detail;
      if (Array.isArray(d)) setServerErr(Object.fromEntries(d.map((x) => [x.loc?.[x.loc.length - 1], x.msg])));
      toast.error(formatApiErrorDetail(d));
    } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalog/bulk/create", { spec: toSpec(f), rows });
      setDone(data); setConfirm(false); toast.success(`${data.created} variants created (hidden)`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || formatApiErrorDetail(d));
      if (d?.preview) setPreview(d.preview);
      setConfirm(false);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="design-created">
        <h1 className="font-display text-4xl font-light text-charcoal">{done.created} {f.design_name} variants created</h1>
        <p className="mt-3 text-sm text-warmgrey">Batch {done.batch_id}. {done.note}</p>
        <p className="mt-2 font-mono text-xs text-charcoal" data-testid="design-created-skus">{done.skus.join(", ")}</p>
        <div className="mt-6 flex gap-3">
          <Button onClick={() => navigate("/admin/products")} data-testid="design-done-products">Back to products</Button>
          <Button variant="outline" onClick={() => { setDone(null); setF(EMPTY); setPreview(null); setRows(null); }} data-testid="design-start-another">Start another design</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="new-design-wizard">
      <Button variant="ghost" onClick={() => navigate("/admin/products")} className="mb-6 -ml-3 gap-2 text-warmgrey" data-testid="design-back"><ArrowLeft className="h-4 w-4" /> All products</Button>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">New product design</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Apply a design across colors</h1>
      <p className="mt-3 max-w-2xl text-sm text-warmgrey">Each color gets a variant on its color product, SKU = prefix + 3-digit color code. Nothing is created until you review the preview and confirm. New variants start hidden until priced.</p>

      <div className="mt-8 grid gap-6 rounded-md border border-border bg-white p-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Field label="1. Design / product name" k="design_name" f={f} set={set} errors={errors} placeholder="e.g. Talia" />
          <div>
            <Label className="text-xs text-warmgrey">2. Colors</Label>
            <div className="mt-1 flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="radio" checked={f.color_scope === "all"} onChange={() => set("color_scope", "all")} data-testid="design-scope-all" /> All active colors ({activeColors.length})</label>
              <label className="flex items-center gap-2"><input type="radio" checked={f.color_scope === "selected"} onChange={() => set("color_scope", "selected")} data-testid="design-scope-selected" /> Select colors</label>
            </div>
            {f.color_scope === "selected" && (
              <div className="mt-2 grid max-h-56 grid-cols-2 gap-1 overflow-y-auto rounded-md border border-border p-2 sm:grid-cols-3" data-testid="design-color-list">
                {colors.filter((c) => c.status !== "INACTIVE").map((c) => (
                  <label key={c.code} className="flex items-center gap-2 text-xs"><Checkbox checked={f.color_codes.includes(c.code)} onCheckedChange={() => toggleColor(c.code)} data-testid={`design-color-${c.code}`} />{c.code} {c.name}</label>
                ))}
              </div>
            )}
            {errors.color_codes && <p className="mt-1 text-xs font-medium text-status-sold" data-testid="design-error-color_codes">{errors.color_codes}</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="3. Format (in)" k="format_in" f={f} set={set} errors={errors} placeholder="1x4" />
            <Field label="Width (mm)" k="width_mm" f={f} set={set} errors={errors} type="number" min="1" />
            <Field label="Length (mm)" k="length_mm" f={f} set={set} errors={errors} type="number" min="1" />
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="4. Pieces per box" k="pieces_per_box" f={f} set={set} errors={errors} type="number" min="1" />
            <Field label="Sq ft per box" k="box_coverage_sqft" f={f} set={set} errors={errors} type="number" step="0.01" min="0.01" />
          </div>
          <Coverage f={f} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-warmgrey">5. Presentation</Label>
              <Select value={f.presentation} onValueChange={(v) => set("presentation", v)}>
                <SelectTrigger className="mt-1 h-10 bg-white" data-testid="design-presentation"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Loose">Loose</SelectItem><SelectItem value="Mesh-mounted">Mesh-mounted</SelectItem></SelectContent>
              </Select>
            </div>
            {f.presentation === "Mesh-mounted" && <Field label="Mesh sheet size" k="mesh_sheet_dims" f={f} set={set} errors={errors} placeholder="e.g. 12x12 in" />}
            <Field label="Finish" k="finish" f={f} set={set} errors={errors} placeholder="e.g. Unglazed" />
            <Field label="SKU prefix (2–4 letters)" k="sku_prefix" f={f} set={set} errors={errors} placeholder="TAL" maxLength={4} />
          </div>
          {f.sku_prefix && <p className="text-xs text-warmgrey" data-testid="design-sku-example">SKU rule: {f.sku_prefix.toUpperCase()} + color code → e.g. {f.sku_prefix.toUpperCase()}001</p>}
          <Button disabled={busy} onClick={() => run(null)} className="bg-charcoal text-white hover:bg-charcoal/85 disabled:cursor-not-allowed disabled:opacity-40" data-testid="design-generate-preview">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate preview"}
          </Button>
          {Object.keys(issues).length > 0 && (
            <p className="text-xs text-warmgrey" data-testid="design-preview-needs">Still needed: {Object.values(issues).join(" · ")}</p>
          )}
        </div>
      </div>

      {preview && (
        <BulkPreviewTable preview={preview} rows={rows} setRows={setRows} busy={busy}
          onRecheck={() => run(rows)} onCreate={() => setConfirm(true)} />
      )}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="border-border bg-white sm:max-w-md" data-testid="design-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light text-charcoal">Create {preview?.counts.ready} {f.design_name} variants?</DialogTitle>
            <DialogDescription>They are created in one batch, hidden from dealers (INACTIVE) until each has a trade price and you make it sellable.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirm(false)} data-testid="design-confirm-cancel">Cancel</Button>
            <Button disabled={busy} onClick={create} className="bg-terracotta text-white hover:bg-terracotta-hover" data-testid="design-confirm-create">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create variants"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
